<template>
  <v-dialog
    v-model="showModal"
    persistent
    max-width="550px"
    @keydown.enter.prevent="submit()"
    @keydown.esc.prevent="cancel()"
  >
    <el-overlay-loading :loading="modalloading" />
    <v-card class="modalCard">
      <v-card-title class="modalTitle pt-2 pb-1 mb-2" v-if="!modalloading">
        <h2>{{ showTitle }}</h2>
      </v-card-title>
      <v-card-text class="pt-2 pb-2">
        <v-row class="pl-0">
          <v-col cols="12">
            <el-autocomplete
              required
              :items="bylosRusysList"
              itemvalue="value"
              itemtext="text"
              return-item-object
              placeholder="Pasirinkite rinkmenos rūšį"
              label="Rinkmenos rūšis"
              tooltip="Modalas rinkmenos. Rinkmenos rūšis"
              v-model="modalUpload.byl_rusis_kla_id"
              autofocus
            />
          </v-col>
          <v-col cols="12" md="6">
            <el-text-field
              required
              label="Dokumento numeris"
              tooltip="Modalas rinkmenos. Dokumento numeris"
              v-model="modalUpload.byl_dokumento_nr"
            />
          </v-col>
          <v-col cols="12" md="6">
            <el-date-picker
              required
              label="Dokumento data"
              tooltip="Modalas rinkmenos. Dokumento data"
              past-dates
              v-model="modalUpload.byl_dokumento_data"
            />
          </v-col>
        </v-row>
        <template v-if="!isByla">
          <div class="upload-label">
            <div class="div-inline">
              <label :class="uploadClass">
                Prisegamos rinkmenos
              </label>
              <div class="div-tooltip">
                <el-tooltip
                  :tooltip="`Modalas rinkmenos. Prisegamos rinkmenos`"
                />
              </div>
            </div>
            <div
              v-if="uploadError"
              class="v-messages theme--light error--text"
              role="alert"
            >
              Laukas privalomas
            </div>
          </div>
          <v-file-input
            class="fileInput"
            ref="fileInput"
            :accept="fileType"
            :multiple="multiple"
            @change="addFiles"
          ></v-file-input>
          <v-card
            ref="dragInput"
            @drop.prevent="onDrop($event)"
            @dragover.prevent="dragover = true"
            @dragenter.prevent="dragover = true"
            @dragleave.prevent="dragover = false"
            @click.prevent="clickUpload"
            :class="dragClass"
            :disabled="!canUpload"
          >
            <v-card-text>
              <v-row
                class="d-flex flex-column"
                dense
                align="center"
                justify="center"
              >
                <v-icon
                  :class="[dragover ? 'mt-2, mb-6' : 'mt-5 ']"
                  color="primary"
                  size="60"
                >
                  mdi-cloud-upload
                </v-icon>
                {{ uploadText }}
              </v-row>
              <v-virtual-scroll
                v-if="isUpload"
                :items="uploadedFiles"
                item-height="60"
              >
                <template v-slot:default="{ item }">
                  <v-list-item :key="item.name">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                        <span class="ml-3 text--secondary">
                          {{ fileSize(item.size) }}</span
                        >
                      </v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action v-if="!instant">
                      <v-btn @click.stop="removeFile(item.name)" icon>
                        <v-icon> mdi-close-circle </v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-virtual-scroll>
              <div>
                <v-progress-linear
                  :value="progress"
                  v-if="isUpload"
                  color="light-blue"
                  height="25"
                  disabled
                >
                  <strong>{{ progress }} %</strong>
                </v-progress-linear>
              </div>
            </v-card-text>
          </v-card>
        </template>
        <template v-if="isByla">
          <v-card>
            <v-card-text class="py-2">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ modalUpload.byl_pavadinimas }}
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn @click.stop="removeUploadedFile()" icon>
                    <v-icon> mdi-close-circle </v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-card-text>
          </v-card>
        </template>
      </v-card-text>
      <v-card-actions class="modalActions" elevation="5">
        <v-spacer></v-spacer>
        <el-button @click.native.stop="closeModal()" :loading="loading">
          Atšaukti
        </el-button>
        <el-button
          primary
          @click.native="saveData()"
          :disabled="!isSaugoti"
          :loading="loading"
        >
          Saugoti
        </el-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ModalUpload',

  props: {
    upload: Object,
    value: Boolean,
    fileType: { type: String, default: '.pdf,.adoc,.asice' },
    bylosRusys: Array,
    multiple: { type: Boolean, default: false },
    instant: { type: Boolean, default: false },
    maxDydis: { type: Number, default: 0 },
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      modalloading: true,
      loading: false,
      modalUpload: {
        byl_rusis_kla_id: null,
        byl_dokumento_nr: null,
        byl_dokumento_data: null,
        byla: null,
      },
      dragover: false,
      progress: 0,
      uploadedFiles: [],
      uploadValidated: true,
    }
  },
  computed: {
    ...mapGetters(['objektasID']),

    showModal: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    showTitle() {
      return 'Rinkmenos prisegimas'
    },
    dragClass() {
      return (this.dragover ? 'blue lighten-1' : '') + 'mb-2'
    },
    isUpload() {
      return this.uploadedFiles.length > 0
    },
    uploadText() {
      return 'Paspauskite arba įtempkite rinkmeną'
    },
    bylosRusysList() {
      return this.bylosRusys.map(item => {
        return item
      })
    },
    isRusis() {
      return this.modalUpload.byl_rusis_kla_id &&
        this.modalUpload.byl_rusis_kla_id > 0
        ? true
        : false
    },
    isDokumentoNr() {
      return this.modalUpload.byl_dokumento_nr &&
        this.modalUpload.byl_dokumento_nr.length > 0
        ? true
        : false
    },
    isDokumentoData() {
      return this.modalUpload.byl_dokumento_data &&
        this.modalUpload.byl_dokumento_data.length > 0
        ? true
        : false
    },
    canUpload() {
      return true //this.isRusis && this.isDokumentoNr && this.isDokumentoData
    },
    isSaugoti() {
      return (
        (this.isUpload ||
          this.isByla ||
          this.isRusis ||
          this.isDokumentoNr ||
          this.isDokumentoData) &&
        !this.instant
      )
    },
    isByla() {
      return this.modalUpload.byl_id > 0 && this.modalUpload.byl_pavadinimas
    },
    isDeleted() {
      return (
        this.modalUpload.byl_id > 0 && this.modalUpload.byl_pavadinimas === null
      )
    },
    fileTypeList() {
      return this.fileType.replaceAll('.', '').split(',')
    },
    maxDydisByte() {
      return this.maxDydis * 1024 * 1024
    },
    uploadClass() {
      return `v-label theme--light ${
        this.uploadRequired ? 'required-text' : ''
      } ${this.uploadError ? 'error--text' : ''}`
    },
    uploadRequired() {
      return 'required'
    },
    uploadError() {
      return !this.uploadValidated
    },
  },
  created() {
    this.modalloading = false
    this.modalUpload = this.upload
    this.modalloading = false
  },
  methods: {
    clickUpload() {
      this.$refs.dragInput.$el.blur()
      this.$refs.fileInput.$refs.input.click()
    },
    addFiles(files) {
      this.dragover = false
      this.uploadedFiles = []
      // files.forEach(element => this.addUploadedFiles(element))
      let fileArray
      if (Array.isArray(files)) {
        fileArray = [...files]
      } else {
        fileArray = [files]
      }
      fileArray.forEach(element => this.addUploadedFiles(element))

      if (this.instant) {
        this.saveData()
      }
    },
    addUploadedFiles(element) {
      let tipas = element.name
        .toLowerCase()
        .split('.')
        .pop()
      if (!this.fileTypeList.includes(tipas)) {
        window._VMA.$emit(
          'DATA_ERROR',
          'Netinkamas dokumento formatas, galima įkelti tik PDF, ADOC ir ASICE'
        )
        return
      }
      if (element.size > this.maxDydisByte) {
        window._VMA.$emit(
          'DATA_ERROR',
          `Leistinas dokumento dydis: ${this.maxDydis} MB`
        )
        return
      }
      this.uploadedFiles.push(element)
      this.validateUpload()
    },
    fileSize(size) {
      if (size < 1024) {
        return size + ' B'
      } else if (size < 1024 * 1024) {
        return Math.round(size / 1024) + ' KB'
      } else if (size < 1024 * 1024 * 1024) {
        return Math.round(size / (1024 * 1024)) + ' MB'
      }
    },

    removeFile(fileName) {
      // Find the index of the
      const index = this.uploadedFiles.findIndex(file => file.name === fileName)

      // If file is in uploaded files remove it
      if (index > -1) this.uploadedFiles.splice(index, 1)
    },
    removeUploadedFile() {
      this.modalUpload.byl_pavadinimas = null
    },

    onDrop(e) {
      this.dragover = false

      // If there are already uploaded files remove them
      if (this.isUpload) this.uploadedFiles = []

      // If user has uploaded multiple files but the component is not multiple throw error
      if (!this.multiple && e.dataTransfer.files.length > 1) {
        window._VMA.$emit(
          'DATA_ERROR',
          'Vienu metu galima įkelti tik vieną doumentą'
        )
      }
      // Add each file to the array of uploaded files
      else {
        // e.dataTransfer.files.forEach(element => this.addUploadedFiles(element))
        const fileArray = [...e.dataTransfer.files]
        fileArray.forEach(element => this.addUploadedFiles(element))
      }

      if (this.instant) {
        this.saveData()
      }
    },
    validateUpload() {
      this.uploadValidated =
        this.isUpload || this.isByla || !this.uploadRequired
    },
    updateProgress(progress) {
      this.progress = progress
    },
    saveData() {
      this.validateUpload()
      this.$validator.validateAll().then(res => {
        if (res && this.uploadValidated) {
          this.loading = true
          const byla = this.uploadedFiles[0]
          let formData = new FormData()

          formData.append('obj_id', this.objektasID)
          if (this.modalUpload.byl_id) {
            formData.append('byl_id', this.modalUpload.byl_id)
          }
          if (this.modalUpload.byl_rusis_kla_id) {
            formData.append(
              'byl_rusis_kla_id',
              this.modalUpload.byl_rusis_kla_id
            )
          }
          if (this.modalUpload.byl_dokumento_nr) {
            formData.append(
              'byl_dokumento_nr',
              this.modalUpload.byl_dokumento_nr
            )
          }
          if (this.modalUpload.byl_dokumento_data) {
            formData.append(
              'byl_dokumento_data',
              this.modalUpload.byl_dokumento_data
            )
          }
          if (this.isUpload) {
            formData.append('byla', byla)
          } else {
            formData.append('byl_deleted', this.isDeleted ? 1 : 0)
          }

          const config = {
            onUploadProgress: progressEvent => {
              this.progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              )
            },
          }

          this.$axios
            .post(`objektas/${this.objektasID}/byla`, formData, config)
            .then(res => {
              window._VMA.$emit('DATA_OK', res.data.message)
              this.$emit('savedData')
              this.closeModal()
            })
            .catch(err => {
              window._VMA.$emit('DATA_ERROR', err.response.data.message)
              this.progress = 0
            })
            .finally(() => {
              this.loading = false
            })
        } else {
          window._VMA.$emit('FORMAT_ERROR')
        }
      })
    },
    closeModal() {
      this.uploadedFiles = []
      this.showModal = false
    },
    submit() {
      if (!this.isSaugoti) {
        this.saveData()
      }
    },
    cancel() {
      this.closeModal()
    },
  },
}
</script>
<style lang="scss" scoped>
.fileInput {
  display: none;
}
.modalCard {
  background: #f5f5f5 !important;
}
.modalTitle {
  background: #f5f5f5 !important;
  position: sticky;
  top: 0;
  z-index: 2;
  background: none;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.modalActions {
  position: sticky;
  bottom: 0;
  z-index: 1;
}
.div-inline {
  display: inline-flex;
  line-height: 20px;
}
.div-tooltip {
  font-size: 14px;
}
.upload-label label {
  font-size: 1.09em;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
}
</style>
